import {
    UPDATE_SEARCH_PARAMETERS,
    UPDATE_SEARCH_RESULTS,
    SET_CONFIGURATION
} from './types'
import { logEvent, sanitizeJsonForLogs } from '../../utilities'
import { graphqlFetch, onDesktop } from '../../components/cdl-react-components/utilities'
import { SearchParameter } from '../../utilities/models'

export const updateKeywordSearchParameters = keyword => {
    return updateSearchParameters([
        new SearchParameter('keyword', encodeURIComponent(keyword))
    ])    
}

export const updateContentTypeSearchParameters = contentType => {
    return updateSearchParameters([
        new SearchParameter('contentTypes', encodeURIComponent(contentType))
    ])    
}

const isConfigValid = (state) => {
    const { configuration } = state.data
    return !!configuration &&
        configuration.hasOwnProperty('ages') &&
        configuration.hasOwnProperty('articleTypes') &&
        configuration.hasOwnProperty('locationOptions') &&
        configuration.hasOwnProperty('mapTypes')
}

/** Converts parameter values to a GraphQL "contains" query */
const parseContainsParam = (findParameter) => (config) => {
    const parser = config.valueParser ?? ((x) => x)
    const param = findParameter(config.param)

    return param ? `{ ${config.graphqlParam}: { contains: "${parser(decodeURIComponent(param.value))}" } }` : undefined
}

/** Converts parameter values to a GraphQL "in" query */
const parseInParam = (findParameter) => (config) => {
    const parser = config.valueParser ?? ((x) => x)
    const param = findParameter(config.param)

    let result = undefined
    if (param) {
        const rawValue = param.value
        const values = typeof rawValue === 'string' ?
            param.value.split(',') :
            param.value        

        const queryTokens = values.map(x => `{ ${config.graphqlParam}: { contains: "${parser(x)}" } }`)
        result = `{ or: [${queryTokens.join(', ')}] }`
    }

    return result
}

/** Converts parameter values to a GraphQL "in" query for NTS location values.
 * We have todo this differently for the NTS, as these would normally be 'between' queries,
 * but our graphql schema doesn't currently support 'lte' or 'gte' comparisons for string values.
 * So instead we find the every value within the specified range and then do an 'in' query on those values. */
const parseNTSInParam = (findParameter, valueStore) => (config) => {    
    const parser = config.valueParser ?? ((x) => x)
    const param = findParameter(config.param)

    let result = undefined
    if (param) {
        const rawValue = param.value
        const value = typeof rawValue === 'string' ?
            param.value.split(',') :
            param.value

        let from = value[0]
        let to = value[1]  

        if (!!from || !!to) {
            // swap from and to if the values are in the wrong order
            if (!!from && !!to && from > to) {
                const temp = from
                from = to
                to = temp
            }

            // get min/max if not defined
            if (!from)
                from = valueStore[0]
            if (!to)
                to = valueStore[valueStore.length - 1]

            const items = valueStore.filter(x => x >= from && x <= to)
            result = `{ ${config.graphqlParam}: { in: [${items.map(x => `"${parser(x)}"`).join(', ')}] } }`            
        }
    }

    return result
}

/** Converts parameter values to a GraphQL "between" query */
const parseBetweenParam = (findParameter) => (config) => {
    const parser = config.valueParser ?? ((x) => x)
    const param = findParameter(config.param)

    let result = undefined
    if (param) {
        let from = param.value[0]
        let to = param.value[1]   

        if (!!from || !!to) {
            // swap from and to if the values are in the wrong order
            if (!!from && !!to && from > to) {
                const temp = from
                from = to
                to = temp
            }

            const tokens = []
            if (!!from)
                tokens.push(isNaN(from) ? `gte: "${parser(from)}"` : `gte: ${parser(from)}`)
            if (!!to)
                tokens.push(isNaN(to) ? `lte: "${parser(to)}"` : `lte: ${parser(to)}`)

            result = `{ ${config.graphqlParam}: { ${tokens.join(', ')} } }`
        }
    }

    return result
}

const getParameterValue = (paramList, id) => 
    paramList.find(x => x.id.toString() === id.toString())?.name

export const updateSearchParameters = searchParameters => {
    return (dispatch, getState) => {
        const startTime = performance.now()
        const firstSearch = !getState().search.firstSearchComplete

        logEvent({ 'New_Search': sanitizeJsonForLogs(searchParameters) })
        dispatch({ type: UPDATE_SEARCH_PARAMETERS, payload: searchParameters })

        // Show search/results
        if (!firstSearch && (searchParameters && searchParameters.length > 0)) {
            const payload =
                onDesktop() ?
                    { sidebarShown: true, subSidebarShown: true } :
                    { sidebarShown: false, subSidebarShown: true }
            dispatch({ type: SET_CONFIGURATION, payload })
        }

        /* Check for valid config. The updateSearchParameter execution from the 
            UrlParameterSync component generally runs before the config values
            have been set to the store; can't run the search without them. - rrogers 2026-02-18 */
        const state = getState()
        if (isConfigValid(state))
        {
            // run search
            const id = state.search.id
            const { ages, articleTypes, mapTypes, locationOptions } = state.data.configuration

            const graphqlTake = 10
            let graphqlSkip = 0
            let graphqlComingSoon = true
            let graphqlParams = undefined            

            if (state.search.parameters && 
                state.search.parameters.length > 0
            ) {
                const findParameter = (name) => state.search.parameters.find(x => x.name === name)
            
                const containsFunc = parseContainsParam(findParameter)
                const inFunc = parseInParam(findParameter)
                const betweenFunc = parseBetweenParam(findParameter)

                const graphqlParaConfig = [
                    { param: 'keyword', graphqlParam: 'searchField', parser: containsFunc },
                    { param: 'contentTypes', graphqlParam: 'contentType', parser: inFunc },
                    { param: 'date', graphqlParam: 'publicationDate', parser: betweenFunc },
                    { param: 'articleTypes', graphqlParam: 'articleTypes', parser: inFunc, valueParser: (x) => getParameterValue(articleTypes, x) },
                    { param: 'mapTypes', graphqlParam: 'mapTypes', parser: inFunc, valueParser: (x) => getParameterValue(mapTypes, x)},
                    { param: 'ages', graphqlParam: 'ages', parser: inFunc, valueParser: (x) => getParameterValue(ages, x) },
                    { param: 'township', graphqlParam: 'township', parser: betweenFunc, valueParser: (x) => parseInt(x) },
                    { param: 'range', graphqlParam: 'range', parser: betweenFunc, valueParser: (x) => parseInt(x) },
                    { param: 'meridian', graphqlParam: 'meridian', parser: betweenFunc, valueParser: (x) => parseInt(x) },
                    { param: 'block', graphqlParam: 'block', parser: parseNTSInParam(findParameter, locationOptions.block) },
                    { param: 'subdivision', graphqlParam: 'subdivision', parser: parseNTSInParam(findParameter, locationOptions.subdivision)},
                    { param: 'mapUnit', graphqlParam: 'mapUnit', parser: parseNTSInParam(findParameter, locationOptions.mapUnit) },
                    { param: 'quad', graphqlParam: 'quad', parser: parseNTSInParam(findParameter, locationOptions.quad) },
                    { param: 'epType', graphqlParam: 'epType', parser: containsFunc }      
                ]

                graphqlParams = 
                    graphqlParaConfig
                        .map(config => config.parser(config))                    
                        .filter(x => !!x)     
                        .join(',\n')               

                const comingSoonParam = findParameter('excludeComingSoon')
                if (comingSoonParam)
                    graphqlComingSoon = !comingSoonParam.value

                const pageParam = findParameter('page')
                if (pageParam)
                    graphqlSkip = (pageParam.value * graphqlTake) - graphqlTake      
            }         

            const query = `
                query {
                    search (
                        includeComingSoon: ${graphqlComingSoon}
                        ${graphqlParams ? `where: { 
                            and: [
                                ${graphqlParams}
                            ] 
                        }` : '' }                        
                        order: { 
                            publicationDate: DESC
                        }
                        take: ${graphqlTake}
                        skip: ${graphqlSkip}
                    ) {
                        totalCount
                        pageInfo {
                            hasNextPage
                            hasPreviousPage
                        }
                        items {
                            id
                            contentType
                            title
                            subtitle
                            publication_date: publicationDate
                            publication_order: publicationOrder
                            articleTypes
                        }                        
                    }
                }`
            //console.log({ query })
            graphqlFetch('wcms3', query)
                .then(res => {
                    /* NOTE: Currently the pagination information is tracked within the <Paginator> 
                    component itself, this isn't a great approach. Ideally the pagination
                    would be a reflection of the 'pageInfo' from the graphql response. 
                    Leaving as is for now, but this is something that should be refactored 
                    in the future. -rrogers 2026-02-17 */
                    const results = {
                        content: res.data.search.items,
                        count: res.data.search.totalCount
                    } 
                    
                    const newState = getState()
                    // is most recent search
                    if (state.search.id === id) {
                        logEvent({ 'Search_Results': newState.search.id, 'Execution_Time': performance.now() - startTime })
                        dispatch({ type: UPDATE_SEARCH_RESULTS, payload: results })
                    }
                })
        }        
    }
}